import './text-editor.css'
import { isUpdateActive} from "../../elements/SuggestionBar/SuggestionCard/mechanism";
import { EditorContent, useEditor } from '@tiptap/react'
import { Mark, mergeAttributes } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import Highlight from '@tiptap/extension-highlight';
import Underline from '@tiptap/extension-underline';
import React, { useEffect, useState, useCallback } from 'react';
import { MenuBar } from './MenuBar';
import { EditorView } from 'prosemirror-view';
import PropTypes from 'prop-types';

const ErrorSquigle = Underline.extend({
  name: 'errorSquigle',
  addOptions() {
    return {
      HTMLAttributes: {
        class: 'error-underline',
      }
    }
  }
});

const RemovedSuggestion = Highlight.extend({
  name: 'removedSuggestion',
  addOptions() {
    return {
      HTMLAttributes: {
        class: 'removed-suggestion',
      }
    }
  }
});

const AddedSuggestion = Highlight.extend({
  name: 'addedSuggestion',
  addOptions() {
    return {
      HTMLAttributes: {
        class: 'added-suggestion',
      }
    }
  }
});

EditorView.prototype.updateState = function updateState(state) {
  if (!this.docView) return; // This prevents the matchesNode error on hot reloads
  this.updateStateInner(state, this.state.plugins !== state.plugins);
};

export const renderHTML = (attributes) => ({
  style: `text-decoration-color: ${attributes.color};`,
});

export const parseHTML = (element) => ({
  color: element.style.textDecorationColor || null,
});

const CustomUnderline = Underline.extend({
  addAttributes() {
    return {
      color: {
        default: null,
        renderHTML: renderHTML,
        parseHTML: parseHTML,
      },
    };
  },
});

const HighlightWord = Mark.create({
  name: 'highlight-word',

  addAttributes() {
    return {
      color: {
        default: null,
        parseHTML: element => element.style.backgroundColor,
        renderHTML: attributes => {
          return { style: `background-color: ${attributes.color}` };
        },
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'span',
        getAttrs: node => node.style.backgroundColor ? { color: node.style.backgroundColor } : {},
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return ['span', mergeAttributes(HTMLAttributes, { style: `background-color: ${HTMLAttributes.color}` }), 0];
  },
});

const TextEditor = ({ onAnalyze, editorRef, isProductDemo }) => {
  const [timer, setTimer] = useState(null);
  const [minimumWords, setMinimumWords] = useState(8);
  const [currentWords, setCurrentWords] = useState([]);
  const [currentTextContent, setCurrentTextContent] = useState("");
  const [isAnalyzed, setIsAnalyzed] = useState(false);

  function handleFastTyping(text) {
    const words = text.split(" ");
    setCurrentWords(words);

    if (currentWords.length >= minimumWords) {
      setMinimumWords(minimumWords + 8);
      onAnalyze(text);
      return true;
    }
    return false;
  }

  const content = isProductDemo
    ? `
      <h2>Halo, Kamu!</h2>
      <p>Berikut ini adalah teks yang kami berikan sebagai demonstrasi, saran perbaikan akan langsung muncul di sebelah kanan ya! </br>
      Halo ges. teks ini hanyc untkk demonstrasi ya, hanya dengna membuat akun kalian dapat langsung mencoba aplikasi ini
      </p>
    `
    : `
      <h2>Halo, Kamu!</h2>
      <p>Ini adalah text biasa yang ditulis di dalam teks box. Kamu bisa menulis teks lain
      dengan mengeklik kotak berwarna putih ini dan mengetik keyboard kamu. Kamu juga bisa
      menghapus teks ini, atau menambahkan teks lain. Tombol-tombol di atas bisa digunakan
      untuk membuat teks menjadi <b>tebal</b>, <i>miring</i>, atau membuat bullet list.</p>
    `;

  const editor = useEditor({
    extensions: [
      StarterKit,
      ErrorSquigle,
      RemovedSuggestion,
      AddedSuggestion,
      CustomUnderline,
      Highlight,
      HighlightWord
    ],
    content,
    editorProps: {
      attributes: {
        spellcheck: 'false',
      },
    },
    onUpdate({editor}){
      if (isUpdateActive()){
        const text = editor.getText();
        
        if (text !== currentTextContent){
          // user is typing fast
          const isSent = handleFastTyping(text);
    
          // user is typing slow
          if (!isSent){
            clearTimeout(timer);
            let newTimer = setTimeout(() => {
              onAnalyze(text);
            }, 500);
            setTimer(newTimer);
          }

          setCurrentTextContent(text);
        }
      }
    },
    editable: !(isProductDemo || false),
  });

  const analyzeDemoContent = useCallback(() => {
    if (isAnalyzed) return;
    const demoText = editor?.getText();
    onAnalyze(demoText);
    setIsAnalyzed(true);
  }, [editor, onAnalyze, isAnalyzed]);

  useEffect(() => {
    if (editor) {
      editorRef.current = editor;
      if (isProductDemo) {
        analyzeDemoContent();
      }
    }
  }, [editor, editorRef, isProductDemo, analyzeDemoContent]);

  return (
    <div data-testid='text-editor' className='text-editor-app'>
      <div className='editor-container'>
        <EditorContent editor={editor} />
      </div>
      <MenuBar editor={editor} />
    </div>
  );
};

TextEditor.propTypes = {
  onAnalyze: PropTypes.func,
  editorRef: PropTypes.object,
  isProductDemo: PropTypes.bool,
};

export default TextEditor;
